import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import {
  CheckCircle2,
  Instagram,
  Linkedin,
  ArrowRight,
  CalendarClock,
  MonitorPlay,
  CalendarCheck,
  Receipt,
  MessageCircle,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMyLatestPaidPayment } from "@/lib/receipts.functions";
import { formatInr } from "@/lib/gst";

export const Route = createFileRoute("/payment-success")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Welcome to WeLivdIt Visa Prep" },
      {
        name: "description",
        content:
          "Your WeLivdIt plan is active. See your enrollment details, your next steps and your tax invoice.",
      },
      { property: "og:title", content: "Welcome to WeLivdIt Visa Prep" },
      {
        property: "og:description",
        content: "Your WeLivdIt visa prep plan is now active. Here's exactly what happens next.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PaymentSuccessPage,
});

const WHATSAPP_URL =
  "https://wa.me/919381256707?text=" +
  encodeURIComponent(
    "Hi WeLivdIt team, I just enrolled in a visa prep plan and my interview is coming up soon. Can you help me schedule my sessions on priority?",
  );

type Payment = {
  id: string;
  plan_name: string;
  amount_paise: number;
  created_at: string;
  buyer_name: string | null;
  buyer_email: string | null;
  razorpay_payment_id: string | null;
  razorpay_order_id: string | null;
  receipt_url: string | null;
};

/** Full Support and Refusal Support include a live DS-160 review session. */
function includesDs160(planName: string | null | undefined) {
  const n = (planName ?? "").toLowerCase();
  return n.includes("full") || n.includes("refusal");
}

function isRefusalPlan(planName: string | null | undefined) {
  return (planName ?? "").toLowerCase().includes("refusal");
}

type Step = { icon: React.ComponentType<{ className?: string }>; title: string; body: string };

function stepsFor(planName: string | null | undefined): Step[] {
  const steps: Step[] = [
    {
      icon: CalendarClock,
      title: "Lock in your visa interview date & consulate",
      body: "Open your dashboard and set your interview date plus your consulate (Hyderabad, Chennai, Mumbai, New Delhi or Kolkata) so we can build your prep timeline around it.",
    },
  ];

  if (includesDs160(planName)) {
    steps.push({
      icon: MonitorPlay,
      title: isRefusalPlan(planName)
        ? "Prepare for your live DS-160 & refusal cross-check"
        : "Prepare for your live DS-160 screen-share audit",
      body: isRefusalPlan(planName)
        ? "Keep your DS-160 and your previous refusal details open during the session. You will share your screen live with the coach while we trace exactly what triggered the 214(b) — nothing to upload or email."
        : "Keep your DS-160 application open during the session. You will share your screen live with the coach and we audit your answers, sponsors and work history line by line — nothing to upload or email.",
    });
  }

  steps.push({
    icon: CalendarCheck,
    title: includesDs160(planName)
      ? "Book your review and mock interview slots"
      : "Book your 1-on-1 mock interview slots",
    body: "Pick your session times from the dashboard. Slots closest to interview dates fill first, so book as early as you can.",
  });

  return steps;
}

function PaymentSuccessPage() {
  const load = useServerFn(getMyLatestPaidPayment);
  const [payment, setPayment] = useState<Payment | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await load();
        if (!active) return;
        if (res.found) setPayment(res.payment as Payment);
      } catch {
        /* Enrollment still succeeded; we just fall back to generic copy. */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [load]);

  const steps = stepsFor(payment?.plan_name);
  const receiptId = payment?.razorpay_payment_id ?? payment?.razorpay_order_id ?? payment?.id ?? null;

  return (
    <main className="mx-auto max-w-2xl px-5 py-14">
      <div className="rounded-3xl border border-border bg-card p-8">
        {/* Welcome header */}
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="h-3.5 w-3.5" /> Payment received
          </span>
          <h1 className="mt-4 font-display text-3xl font-semibold sm:text-4xl">Welcome to WeLivdIt Visa Prep 🎉</h1>
          <p className="mt-3 text-muted-foreground">
            Your enrollment is confirmed and a confirmation email is on its way. Here is exactly what happens next.
          </p>
        </div>

        {/* Enrollment details */}
        <div className="mt-7 rounded-2xl border border-border bg-muted/40 p-5 text-left">
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Enrollment details</h2>
          {loading ? (
            <div className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading your enrollment…
            </div>
          ) : payment ? (
            <dl className="mt-3 space-y-2 text-sm">
              {payment.buyer_name && (
                <Row label="Student">
                  {payment.buyer_name}
                  {payment.buyer_email ? ` · ${payment.buyer_email}` : ""}
                </Row>
              )}
              <Row label="Plan">
                {payment.plan_name} · {formatInr(payment.amount_paise / 100)}
              </Row>
              <Row label="Status">
                <span className="font-semibold text-emerald-700 dark:text-emerald-400">🟢 Active &amp; confirmed</span>
              </Row>
            </dl>
          ) : (
            <p className="mt-3 text-sm text-foreground/75">
              Your plan is active. Open your dashboard to see your enrollment and receipt.
            </p>
          )}

          {receiptId && (
            <Button asChild variant="outline" size="sm" className="mt-4 rounded-full">
              <Link to="/receipt/$id" params={{ id: receiptId }}>
                <Receipt className="mr-1.5 h-4 w-4" /> View / download tax invoice
              </Link>
            </Button>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            A copy of your receipt has also been emailed to you, and you can re-download it anytime from your
            dashboard.
          </p>
        </div>

        {/* Next steps */}
        <div className="mt-7 text-left">
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            Your next {steps.length} steps
          </h2>
          <ol className="mt-4 space-y-4">
            {steps.map((s, i) => (
              <li key={s.title} className="flex gap-3.5">
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                  <s.icon className="h-4.5 w-4.5" />
                </span>
                <div>
                  <div className="text-sm font-semibold">
                    {i + 1}. {s.title}
                  </div>
                  <p className="mt-1 text-sm leading-relaxed text-foreground/75">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Primary CTA */}
        <div className="mt-7 flex flex-wrap justify-center gap-3">
          <Button asChild size="lg" className="rounded-full bg-gradient-cta font-semibold text-primary-foreground">
            <Link to="/student-dashboard">
              Go to dashboard &amp; start prep <ArrowRight className="ml-1.5 h-4 w-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="rounded-full">
            <Link to="/">Back home</Link>
          </Button>
        </div>

        {/* Priority WhatsApp bridge */}
        <div className="mt-7 rounded-2xl border border-emerald-600/25 bg-emerald-500/5 p-5 text-left">
          <div className="text-sm font-semibold">Interview in the next 7 days?</div>
          <p className="mt-1 text-sm text-foreground/75">
            Message our coach team on WhatsApp and we will fast-track your session scheduling.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            <MessageCircle className="h-4 w-4" /> Chat with the coach team
          </a>
        </div>

        {/* Socials */}
        <div className="mt-7 flex items-center justify-center gap-3">
          <a
            href="https://www.instagram.com/welivdit"
            target="_blank"
            rel="noreferrer"
            aria-label="WeLivdIt on Instagram"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border hover:bg-muted"
          >
            <Instagram className="h-4 w-4" />
          </a>
          <a
            href="https://www.linkedin.com/company/welivdit"
            target="_blank"
            rel="noreferrer"
            aria-label="WeLivdIt on LinkedIn"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border hover:bg-muted"
          >
            <Linkedin className="h-4 w-4" />
          </a>
        </div>

        {/* Refund policy — unchanged */}
        <div className="mt-8 rounded-2xl border border-border bg-muted/40 p-5 text-left">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Refund policy</h2>
          <p className="mt-2 text-sm text-foreground/85">
            Eligible for a 100% full refund within 30 days of payment if NO sessions (DS-160 review or Mock Interviews)
            are booked or conducted. Once ANY session is booked or conducted, fees are strictly non-refundable. Read our
            WeLivdIt Refund Policy at{" "}
            <a
              href="https://welivdit.online/refund-policy"
              target="_blank"
              rel="noreferrer"
              className="text-primary underline"
            >
              welivdit.online/refund-policy
            </a>
            .
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            For example, if a person enrolled in Basic/Full Support/Refusal Support plans,did not attend any sessions,
            and requested a cancellation within 30 days, a 100% full refund will be processed.
          </p>
        </div>
      </div>
    </main>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="text-muted-foreground">{label}:</dt>
      <dd className="font-medium">{children}</dd>
    </div>
  );
}
